/**
 * PATH: packages/ui/src/components/settings/BrandingTab.tsx
 * PURPOSE: Manage organisation branding (logo, colors, fonts)
 *
 * WHY: Consistent branding across exports and dashboards requires an
 *      admin-friendly configuration surface.
 *
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌──────────────┐
 *   │ Load Styles │───▶│ Edit Branding│───▶│ Save Updates │
 *   └─────────────┘    └──────────────┘    └──────────────┘
 *
 * DEPENDENCIES:
 *   - SettingsApiClient: API contract for persistence
 *   - lucide-react: icons
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { ImageIcon, Palette, Type, Loader2, Check } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { SettingsApiClient } from './types'

interface BrandingState {
  logo_url?: string | null
  logo_display_height_px: number
  color_primary: string
  color_secondary: string
  color_accent: string
  color_background: string
  color_text: string
  font_heading: string
  font_body: string
  font_mono: string
  tagline: string
  website: string
  linkedin_url: string
}

export interface BrandingTabProps {
  apiClient?: SettingsApiClient
  endpoint?: string
  isLoading?: boolean
  isSaving?: boolean
  onSave?: (data: BrandingState) => Promise<void>
  className?: string
}

const DEFAULT_BRANDING: BrandingState = {
  logo_url: null,
  logo_display_height_px: 46,
  color_primary: '#1E40AF',
  color_secondary: '#3B82F6',
  color_accent: '#10B981',
  color_background: '#F8FAFC',
  color_text: '#1E293B',
  font_heading: 'Inter',
  font_body: 'Inter',
  font_mono: 'JetBrains Mono',
  tagline: '',
  website: '',
  linkedin_url: '',
}

export function BrandingTab({
  apiClient,
  // NOTE: apiClient baseURL already includes /api, so endpoint should NOT include /api prefix
  endpoint = '/organisation/branding/',
  isLoading = false,
  isSaving = false,
  onSave,
  className,
}: BrandingTabProps) {
  const [branding, setBranding] = useState<BrandingState>(DEFAULT_BRANDING)
  const [remoteLoading, setRemoteLoading] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  const isReady = useMemo(() => !!apiClient || !!onSave, [apiClient, onSave])

  useEffect(() => {
    if (!apiClient) return
    let isMounted = true
    setRemoteLoading(true)
    apiClient.get(endpoint)
      .then((response) => {
        if (!isMounted) return
        const data = response?.data ?? response
        if (data) {
          setBranding(prev => ({ ...prev, ...data }))
          setHasChanges(false)
        }
      })
      .catch(() => {
        if (!isMounted) return
      })
      .finally(() => {
        if (!isMounted) return
        setRemoteLoading(false)
      })
    return () => {
      isMounted = false
    }
  }, [apiClient, endpoint])

  const handleChange = useCallback((key: keyof BrandingState, value: BrandingState[keyof BrandingState]) => {
    setBranding(prev => ({ ...prev, [key]: value }))
    setHasChanges(true)
  }, [])

  const handleSave = useCallback(async () => {
    if (onSave) {
      await onSave(branding)
      setHasChanges(false)
      return
    }
    if (apiClient?.patch) {
      await apiClient.patch(endpoint, branding)
      setHasChanges(false)
    }
  }, [apiClient, branding, endpoint, onSave])

  const handleLogoUpload = async (file: File) => {
    if (!apiClient?.post) return
    const formData = new FormData()
    formData.append('file', file)
    formData.append('logo_type', 'primary')
    // NOTE: apiClient baseURL already includes /api, so endpoint should NOT include /api prefix
    await apiClient.post('/organisation/branding/logo/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    const refreshed = await apiClient.get(endpoint)
    const data = refreshed?.data ?? refreshed
    setBranding(prev => ({ ...prev, ...data }))
  }

  if (isLoading || remoteLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    )
  }

  if (!isReady) {
    return (
      <div className={cn('p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40', className)}>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Branding settings are not available in this hub.
        </p>
      </div>
    )
  }

  return (
    <div className={cn('space-y-6 max-w-3xl', className)}>
      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Branding</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Control organisation colors, fonts, and logo used across Quotech.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <ImageIcon className="w-5 h-5 text-slate-400" />
            <h4 className="font-medium text-slate-800 dark:text-slate-200">Logo</h4>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 flex items-center justify-center bg-slate-50 dark:bg-slate-900">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
              ) : (
                <ImageIcon className="w-6 h-6 text-slate-400" />
              )}
            </div>
            <label className="text-sm font-medium text-slate-600 dark:text-slate-300 cursor-pointer">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleLogoUpload(file)
                }}
              />
              Upload logo
            </label>
          </div>
          <label className="mt-4 block text-sm text-slate-600 dark:text-slate-300">
            Logo height on quote PDF and ITP (24-80px)
            <input
              type="number"
              min={24}
              max={80}
              value={branding.logo_display_height_px}
              onChange={(e) => handleChange('logo_display_height_px', parseInt(e.target.value, 10))}
              className="mt-1 w-full px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
            />
          </label>
        </div>

        <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <Type className="w-5 h-5 text-slate-400" />
            <h4 className="font-medium text-slate-800 dark:text-slate-200">Typography</h4>
          </div>
          <div className="space-y-3">
            <input
              value={branding.font_heading}
              onChange={(e) => handleChange('font_heading', e.target.value)}
              placeholder="Heading font"
              className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
            />
            <input
              value={branding.font_body}
              onChange={(e) => handleChange('font_body', e.target.value)}
              placeholder="Body font"
              className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
            />
            <input
              value={branding.font_mono}
              onChange={(e) => handleChange('font_mono', e.target.value)}
              placeholder="Monospace font"
              className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
            />
          </div>
        </div>
      </div>

      <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3 mb-4">
          <Palette className="w-5 h-5 text-slate-400" />
          <h4 className="font-medium text-slate-800 dark:text-slate-200">Colors</h4>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {([
            { key: 'color_primary', label: 'Primary' },
            { key: 'color_secondary', label: 'Secondary' },
            { key: 'color_accent', label: 'Accent' },
            { key: 'color_background', label: 'Background' },
            { key: 'color_text', label: 'Text' },
          ] as const).map((item) => (
            <div key={item.key} className="flex items-center gap-3">
              <input
                type="color"
                value={branding[item.key]}
                onChange={(e) => handleChange(item.key, e.target.value)}
                className="w-12 h-10 p-1 border border-slate-200 dark:border-slate-700 rounded-lg"
              />
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{item.label}</p>
                <p className="text-xs text-slate-400">{branding[item.key]}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <input
          value={branding.tagline}
          onChange={(e) => handleChange('tagline', e.target.value)}
          placeholder="Tagline"
          className="px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
        />
        <input
          value={branding.website}
          onChange={(e) => handleChange('website', e.target.value)}
          placeholder="Website"
          className="px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
        />
        <input
          value={branding.linkedin_url}
          onChange={(e) => handleChange('linkedin_url', e.target.value)}
          placeholder="LinkedIn URL"
          className="px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
          className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
        </button>
        {!isSaving && !hasChanges && (
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <Check className="w-4 h-4" /> Saved
          </span>
        )}
      </div>
    </div>
  )
}

export default BrandingTab
